import { useState, useRef, useCallback } from 'react';
import ApiService from '../services/ApiService';

/**
 * Reusable hook for like/unlike functionality
 * Works with Articles, Events, Reviews, and any entity with like capability
 *
 * Features:
 * - Prevents double-clicks using synchronous ref check
 * - Optimistic UI updates with rollback on error
 * - Configurable API endpoints per entity type
 *
 * @param {Object} options
 * @param {string} options.entityType - 'article' | 'event' | 'review'
 * @param {Function} options.isLoggedIn - Function that returns true if user is logged in
 * @param {Function} options.onAuthRequired - Callback when auth is required but user not logged in
 * @param {Function} options.onError - Callback on error (receives error message)
 * @param {Function} options.onSuccess - Optional callback on successful like/unlike
 */
const useLike = ({
  entityType = 'article',
  isLoggedIn,
  onAuthRequired,
  onError,
  onSuccess,
}) => {
  // Track which entities user has liked
  const [likedEntities, setLikedEntities] = useState({});

  // Track like counts per entity
  const [likeCounts, setLikeCounts] = useState({});

  // Synchronous ref to prevent double-clicks (useState is async and causes race conditions)
  const processingRef = useRef({});

  // API endpoints for different entity types
  const getEndpoint = useCallback((entityId, type) => {
    const endpoints = {
      article: `blog/${entityId}/like`,
      event: `events/${entityId}/favourite`,
      review: `events/feedback/like-review/${entityId}`,
      good: `goods/community/${entityId}/like`,
    };
    return endpoints[type] || endpoints.article;
  }, []);

  // Get HTTP method for different entity types
  const getMethod = useCallback((type) => {
    const methods = {
      article: 'post',
      event: 'put',
      review: 'put',
      good: 'post',
    };
    return methods[type] || 'post';
  }, []);

  /**
   * Initialize like state for an entity
   * Call this when loading entities to set their initial like state
   */
  const initializeLikeState = useCallback((entityId, isLiked, count = 0) => {
    setLikedEntities(prev => ({ ...prev, [entityId]: isLiked }));
    setLikeCounts(prev => ({ ...prev, [entityId]: count }));
  }, []);

  /**
   * Batch initialize like states for multiple entities
   */
  const initializeBatch = useCallback((entities, getLiked, getCount) => {
    const likedMap = {};
    const countMap = {};

    entities.forEach(entity => {
      const id = entity._id || entity.id;
      likedMap[id] = getLiked(entity);
      countMap[id] = getCount(entity);
    });

    setLikedEntities(prev => ({ ...prev, ...likedMap }));
    setLikeCounts(prev => ({ ...prev, ...countMap }));
  }, []);

  /**
   * Toggle like for an entity
   * This is the main function to call when user clicks like button
   */
  const toggleLike = useCallback(async (entityId) => {
    console.log('=== useLike toggleLike called ===');
    console.log('entityId:', entityId);
    console.log('entityType:', entityType);

    // Check authentication
    if (isLoggedIn && !isLoggedIn()) {
      console.log('Auth check failed - user not logged in');
      onAuthRequired?.();
      return { success: false, reason: 'auth_required' };
    }
    console.log('Auth check passed');

    // CRITICAL: Synchronous double-click prevention using ref
    // This must happen BEFORE any async operations
    if (processingRef.current[entityId]) {
      console.log('Already processing - skipping');
      return { success: false, reason: 'in_progress' };
    }

    // Lock immediately (synchronous - this is why we use ref instead of state)
    processingRef.current[entityId] = true;

    // Capture current state for potential rollback
    const wasLiked = likedEntities[entityId] || false;
    const previousCount = likeCounts[entityId] || 0;
    console.log('wasLiked:', wasLiked, 'previousCount:', previousCount);

    // Optimistic update - update UI immediately
    setLikedEntities(prev => ({
      ...prev,
      [entityId]: !wasLiked
    }));

    setLikeCounts(prev => ({
      ...prev,
      [entityId]: wasLiked ? Math.max(0, previousCount - 1) : previousCount + 1
    }));

    try {
      const endpoint = getEndpoint(entityId, entityType);
      const method = getMethod(entityType);
      console.log('Making API call:', method.toUpperCase(), endpoint);

      // Make API call - events need { favourite: bool } in body
      let response;
      if (method === 'post') {
        response = await ApiService.post(endpoint);
      } else if (entityType === 'event') {
        response = await ApiService.put(endpoint, { favourite: !wasLiked });
      } else {
        response = await ApiService.put(endpoint);
      }
      console.log('API response:', response?.data);

      onSuccess?.(!wasLiked, entityId);
      return { success: true, isLiked: !wasLiked };

    } catch (error) {
      console.error(`Error toggling like for ${entityType}:`, error);
      console.error('Error details:', error.response?.data || error.message);

      // Rollback on error
      setLikedEntities(prev => ({
        ...prev,
        [entityId]: wasLiked
      }));

      setLikeCounts(prev => ({
        ...prev,
        [entityId]: previousCount
      }));

      onError?.('Failed to update like. Please try again.');
      return { success: false, reason: 'api_error', error };

    } finally {
      // Always unlock (synchronous)
      processingRef.current[entityId] = false;
    }
  }, [entityType, isLoggedIn, onAuthRequired, onError, onSuccess, likedEntities, likeCounts, getEndpoint, getMethod]);

  /**
   * Check if an entity is currently liked
   */
  const isLiked = useCallback((entityId) => {
    return likedEntities[entityId] || false;
  }, [likedEntities]);

  /**
   * Get like count for an entity
   */
  const getLikeCount = useCallback((entityId) => {
    return likeCounts[entityId] || 0;
  }, [likeCounts]);

  /**
   * Check if a like operation is in progress for an entity
   */
  const isProcessing = useCallback((entityId) => {
    return processingRef.current[entityId] || false;
  }, []);

  /**
   * Update like count directly (useful when receiving updated data from server)
   */
  const updateLikeCount = useCallback((entityId, count) => {
    setLikeCounts(prev => ({ ...prev, [entityId]: count }));
  }, []);

  /**
   * Set liked state directly (useful when receiving updated data from server)
   */
  const setLiked = useCallback((entityId, liked) => {
    setLikedEntities(prev => ({ ...prev, [entityId]: liked }));
  }, []);

  return {
    // State
    likedEntities,
    likeCounts,

    // Actions
    toggleLike,
    initializeLikeState,
    initializeBatch,
    updateLikeCount,
    setLiked,

    // Getters
    isLiked,
    getLikeCount,
    isProcessing,
  };
};

export default useLike;
